import React from "react";
import {
	Button,
	ClassNameProps,
	Column,
	compareObjects,
	compareStringsDesc,
	FormattedText,
	Heading,
	If,
	Modal,
	pickKeys,
	relativeTimeShort,
	RelativeTimestamp,
	Row,
	sort,
	StyleProps,
	Tooltip,
	TooltipButton,
	useSync,
} from "@hex-insights/core";
import { ValidationDisplayPolicy } from "@hex-insights/forms";
import { Icon } from "../../../../Components/Icon";
import {
	MediaAttachment,
	Newsletter,
	Post,
	SocialComment,
	User,
	UserNameAndImageFragment,
	UserPublicNameFormat,
	useViewerPublicNameFormatQuery,
} from "../../../../Utilities";
import { SocialCommentDeleteButton } from "../../../DeleteButtons";
import { UserFormat } from "../../../Format";
import { SocialCommentForm } from "../../../Forms";
import { UserLink } from "../../../Links";
import * as UserComponents from "../../User";
import styles from "./styles.module.css";

export type CommentInView = Pick<SocialComment, "id" | "body" | "createdAt"> & {
	author: (Pick<User, "id"> & UserNameAndImageFragment) | null;
};

export type CommentsViewProps = {
	mediaAttachmentID?: MediaAttachment["id"] | null;
	newsletterID?: Newsletter["id"] | null;
	postID?: Post["id"] | null;
	comments: CommentInView[];
} & Partial<ClassNameProps & StyleProps>;

export function CommentsView({
	mediaAttachmentID = null,
	newsletterID = null,
	postID = null,
	comments,
	className,
	style,
}: CommentsViewProps) {
	const { syncID, synchronize } = useSync();

	const { data: viewerData } = useViewerPublicNameFormatQuery();
	const canComment = !!viewerData && viewerData?.viewer?.publicNameFormat !== UserPublicNameFormat.Hidden;

	const formStateArgs = React.useMemo(
		() => ({
			initialFormValues: {
				mediaAttachmentID,
				newsletterID,
				postID,
			},
			validationDisplayPolicy: ValidationDisplayPolicy.none,
		}),
		[mediaAttachmentID, newsletterID, postID],
	);

	// sort because newly created comments will not be placed in order
	const sortedComments = React.useMemo(
		() => sort(comments, compareObjects("createdAt", compareStringsDesc())),
		[comments],
	);

	return (
		<Column justify="spaced-start" verticalSpacing="1rem" className={className} style={style}>
			<div className={styles["comments__form-container"]}>
				<SocialCommentForm.ControlledCreateOnExisting
					key={syncID}
					formStateArgs={formStateArgs}
					formNameSuffix={mediaAttachmentID ?? newsletterID ?? postID ?? undefined}
					onSuccess={synchronize}
					noCloseConfirmation
					noNavigationPrompt
					mergeUnsavedChangesAutomatically
					noNotifications
					disabled={!canComment}
				/>
			</div>

			<CommentsList comments={sortedComments} />
		</Column>
	);
}

type CommentsListProps = {
	comments: CommentInView[];
} & Partial<ClassNameProps & StyleProps>;

export function CommentsList({ comments, className, style }: CommentsListProps) {
	return (
		<Column justify="spaced-start" className={className} style={style}>
			{comments.map((comment) => (
				<CommentView key={comment.id} comment={comment} />
			))}
		</Column>
	);
}

const editFormStateArgs = { initialIsEditing: true };

type CommentViewProps = {
	comment: CommentInView;
};

function CommentView({ comment }: CommentViewProps) {
	const viewerIsAuthor = comment.author?.isCurrent ?? false;

	const { isModalOpen: isEditModalOpen, openModal: openEditModal, closeModal: closeEditModal } = Modal.useToggle();
	const commentForEdit = React.useMemo(() => pickKeys(comment, ["id", "body"]), [comment]);

	return (
		<Row
			justify="spaced-start"
			align="center"
			horizontalSpacing="0.5rem"
			className={viewerIsAuthor ? styles["comments__comment--viewer-author"] : ""}
		>
			{comment.author && <UserComponents.UserIcon user={comment.author} size={28} />}

			<Column justify="spaced-start" verticalSpacing="0.125rem" className={styles["comments__comment__box"]}>
				<Row justify="space-between" align="flex-start" className={styles["comments__comment__header"]}>
					{comment.author && (
						<UserLink
							instance={comment.author}
							className={styles["comments__comment__author"] + " link--no-text-decoration"}
						>
							{UserFormat.name(comment.author)}
						</UserLink>
					)}

					<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem">
						<RelativeTimestamp
							value={comment.createdAt}
							format={relativeTimeShort}
							className={styles["comments__comment__timestamp"]}
						/>
						<If condition={viewerIsAuthor}>
							<TooltipButton>
								{() => (
									<Tooltip isInteractable>
										<Tooltip.Body>
											<Column justify="spaced-start" align="center" verticalSpacing="0.25rem">
												<Button variant="link" size="small" onClick={openEditModal}>
													Edit
												</Button>
												<SocialCommentDeleteButton instanceID={comment.id} size="small">
													Delete
												</SocialCommentDeleteButton>
											</Column>
										</Tooltip.Body>
									</Tooltip>
								)}
								{({ onClick, disabled }) => (
									<Button
										variant="link"
										size="small"
										onClick={onClick}
										disabled={disabled}
										className={styles["comments__comment__menu-button"]}
									>
										<Icon.DotsThree size={16} className="block" />
									</Button>
								)}
							</TooltipButton>
						</If>
					</Row>
				</Row>
				<FormattedText>{comment.body}</FormattedText>
			</Column>

			<Modal.If condition={isEditModalOpen}>
				<Modal onClose={closeEditModal}>
					<Modal.Header>
						<Heading.H2 noMargin>Edit Comment</Heading.H2>
					</Modal.Header>
					<Modal.Body>
						<SocialCommentForm.ControlledDetailOnExisting
							socialComment={commentForEdit}
							onSuccess={closeEditModal}
							formStateArgs={editFormStateArgs}
							noChangesDialog
							noLocalStorage
							style={{ width: "100%", "--text-field---width": "100%" } as React.CSSProperties}
						/>
					</Modal.Body>
				</Modal>
			</Modal.If>
		</Row>
	);
}
